import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { Brain, CalendarClock, History, Landmark, Inbox, Loader2, MessagesSquare, Minus, Maximize2, Newspaper, PanelRight, PictureInPicture2, Sparkles, Target, X, BarChart3 } from 'lucide-react';
import { useKlarysFenetre } from './useKlarysFenetre';
import { useAuth } from '../../contexts/AuthContext';
import { KlarysChat } from './KlarysChat';
import { KlarysJournal } from './KlarysJournal';
import { KlarysTaches } from './KlarysTaches';
import { KlarysEnvois } from './KlarysEnvois';
import { KlarysObjectifs } from './KlarysObjectifs';
import { KlarysPartages } from './KlarysPartages';
import { KlarysMemoire } from './KlarysMemoire';
import { KlarysAttribuer } from './KlarysAttribuer';
import { klarysUrl } from './klarysApi';

const RESUME_DOC = 'Résume ce document : nature, parties concernées, montants, dates importantes, références et points d\'attention éventuels.';

export const KlarysLauncher = () => {
  const { user, getAuthHeader } = useAuth();
  const { pathname } = useLocation();
  const [ouvert, setOuvert] = useState(false);
  const [reduit, setReduit] = useState(false);
  const [pret, setPret] = useState(false);
  const fen = useKlarysFenetre();
  const reduitRef = React.useRef(false);
  reduitRef.current = reduit;
  useEffect(() => {
    const surPret = () => { if (reduitRef.current) setPret(true); };
    window.addEventListener('klarys-reponse-prete', surPret);
    return () => window.removeEventListener('klarys-reponse-prete', surPret);
  }, []);
  useEffect(() => { if (!reduit) setPret(false); }, [reduit]);
  const [vue, setVue] = useState('chat');
  const [cible, setCible] = useState(null);
  const [chargeRapport, setChargeRapport] = useState(false);
  const superAdmin = user?.role === 'super_admin';
  const autorise = (superAdmin || user?.role === 'admin') && pathname.startsWith('/admin') && !pathname.startsWith('/admin/login');

  const ouvrirRapport = async () => {
    setOuvert(true);
    setChargeRapport(true);
    try {
      const r = await axios.get(klarysUrl('/rapport'), { headers: getAuthHeader() });
      setVue('chat');
      setCible(r.data.conversation_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rapport indisponible');
    } finally {
      setChargeRapport(false);
    }
  };

  useEffect(() => {
    if (autorise && new URLSearchParams(window.location.search).get('klarys') === 'rapport') ouvrirRapport();
  }, [autorise]); // eslint-disable-line react-hooks/exhaustive-deps

  const [nonLus, setNonLus] = useState(0);
  const [demandeExterne, setDemandeExterne] = useState(null);
  const [aAttribuer, setAAttribuer] = useState(0);
  const compterPartages = () => {
    axios.get(klarysUrl('/partages'), { headers: getAuthHeader() }).then((r) => setNonLus(r.data.non_lus)).catch(() => {});
    axios.get(klarysUrl('/virements'), { headers: getAuthHeader() }).then((r) => setAAttribuer(r.data.nb_a_attribuer)).catch(() => {});
  };
  useEffect(() => {
    if (!autorise) return undefined;
    compterPartages();
    const t = setInterval(compterPartages, 60000);
    window.addEventListener('klarys-compteurs', compterPartages);
    const surDocument = (e) => { setOuvert(true); setVue('chat'); setDemandeExterne({ texte: RESUME_DOC, gedId: e.detail.gedId, nomDoc: e.detail.nom, nonce: Date.now() }); };
    window.addEventListener('klarys-demander-document', surDocument);
    return () => { clearInterval(t); window.removeEventListener('klarys-compteurs', compterPartages); window.removeEventListener('klarys-demander-document', surDocument); };
  }, [autorise]); // eslint-disable-line react-hooks/exhaustive-deps
  const estMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
  const raccourci = estMac ? '⌘ K' : 'Ctrl K';
  useEffect(() => {
    if (!autorise) return undefined;
    const surTouche = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOuvert(true);
        setReduit(false);
        setVue('chat');
        setTimeout(() => document.querySelector('[data-testid="klarys-input"]')?.focus(), 80);
      } else if (e.key === 'Escape' && !document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) {
        setOuvert(false);
        setReduit(false);
      }
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [autorise]);
  useEffect(() => {
    if (ouvert && !reduit && !fen.flottant && autorise) document.body.dataset.klarys = 'ouvert';
    else delete document.body.dataset.klarys;
    return () => { delete document.body.dataset.klarys; };
  }, [ouvert, reduit, autorise, fen.flottant]);
  const lancerDemande = (texte, modeleTitre = null) => { setVue('chat'); setDemandeExterne({ texte, modeleTitre, nonce: Date.now() }); };

  if (!autorise) return null;
  const onglet = (k, l, Icon, badge = 0) => (
    <button type="button" onClick={() => setVue(k)} data-testid={`klarys-tab-${k}`}
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-xs ${vue === k ? 'bg-white/15 text-white' : 'text-sky-100 hover:text-white'}`}>
      <Icon className="h-3.5 w-3.5" /> {l}
      {badge > 0 && <span className="ml-0.5 rounded-full bg-amber-300 px-1.5 text-[10px] font-bold text-[#0a3d62]" data-testid={`klarys-tab-${k}-badge`}>{badge}</span>}
    </button>
  );
  return (
    <>
      {!ouvert && (
        <button type="button" onClick={() => setOuvert(true)} data-testid="klarys-launcher"
          title={`Ouvrir Klarys (${estMac ? 'Cmd' : 'Ctrl'}+K)`} aria-keyshortcuts={estMac ? 'Meta+K' : 'Control+K'}
          className="fixed bottom-6 right-[6.5rem] z-[60] inline-flex items-center gap-2 rounded-full bg-[#0a3d62] px-4 py-3 text-sm font-semibold text-white shadow-xl shadow-[#0a3d62]/30 transition-transform hover:-translate-y-0.5">
          <Sparkles className="h-4 w-4 text-amber-300" /> Klarys
          <kbd className="rounded border border-white/25 bg-white/10 px-1.5 py-0.5 font-sans text-[10px] font-medium text-sky-100" data-testid="klarys-launcher-shortcut">{raccourci}</kbd>
          {nonLus > 0 && <span className="rounded-full bg-amber-300 px-1.5 text-[11px] font-bold text-[#0a3d62]" data-testid="klarys-launcher-badge">{nonLus}</span>}
          {aAttribuer > 0 && <span className="rounded-full bg-rose-400 px-1.5 text-[11px] font-bold text-white" title="Virements à attribuer" data-testid="klarys-launcher-badge-virements">{aAttribuer}</span>}
        </button>
      )}
      {ouvert && reduit && (
        <div className="fixed bottom-6 right-[6.5rem] z-[60] inline-flex items-center gap-1 rounded-full bg-[#0a3d62] pl-4 pr-1.5 py-1.5 text-sm font-semibold text-white shadow-xl shadow-[#0a3d62]/30" data-testid="klarys-reduit-bar">
          <button type="button" onClick={() => setReduit(false)} className="inline-flex items-center gap-2 py-1.5" data-testid="klarys-agrandir" title={pret ? 'Réponse de Klarys prête — agrandir' : 'Agrandir Klarys'}>
            <span className="relative">
              <Sparkles className="h-4 w-4 text-amber-300" />
              {pret && <span className="absolute -right-1.5 -top-1.5 flex h-3 w-3" data-testid="klarys-reponse-prete-dot">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-[#0a3d62]" />
              </span>}
            </span>
            Klarys
            <span className="text-[11px] font-normal text-sky-100">{pret ? 'réponse prête' : 'réduit'}</span>
            <Maximize2 className="h-3.5 w-3.5 text-sky-100" />
          </button>
          <button type="button" onClick={() => { setOuvert(false); setReduit(false); }} className="ml-1 rounded-full p-1.5 hover:bg-white/10" data-testid="klarys-reduit-close" aria-label="Fermer" title="Fermer">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      {ouvert && (
        <div ref={fen.ref} style={fen.style} className={`${fen.classe} ${reduit ? 'hidden' : ''}`} data-testid="klarys-panel">
          <header onMouseDown={fen.glisser} className={`flex items-center gap-3 bg-[#0a3d62] px-4 py-3 text-white ${fen.flottant ? 'cursor-move select-none rounded-t-xl' : ''}`}>
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-300/20"><Sparkles className="h-4 w-4 text-amber-300" /></span>
            <div>
              <p className="text-sm font-bold leading-tight">Klarys</p>
              <p className="whitespace-nowrap text-[11px] text-sky-100">Assistant IA</p>
            </div>
            <nav className="ml-2 flex min-w-0 flex-1 gap-0.5 overflow-x-auto [scrollbar-width:none]">{onglet('chat', 'Conversation', MessagesSquare)}{onglet('partages', 'Partagées', Inbox, nonLus)}{onglet('attribuer', 'À attribuer', Landmark, aAttribuer)}{onglet('memoire', 'Mémoire', Brain)}{onglet('objectifs', 'Paramètres', Target)}{onglet('envois', 'Envois', BarChart3)}{onglet('taches', 'Tâches', CalendarClock)}{onglet('journal', 'Journal', History)}</nav>
            <button type="button" onClick={ouvrirRapport} disabled={chargeRapport} data-testid="klarys-rapport-btn"
              title="Rapport du jour" className="ml-1 inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-amber-300 px-2.5 py-1 text-xs font-semibold text-[#0a3d62] hover:bg-amber-200 disabled:opacity-60">
              {chargeRapport ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Newspaper className="h-3.5 w-3.5" />} Rapport
            </button>
            <button type="button" onClick={fen.basculer} className="shrink-0 rounded-md p-1.5 hover:bg-white/10" data-testid="klarys-fenetre-toggle"
              aria-label={fen.flottant ? 'Ancrer à droite' : 'Fenêtre flottante'} title={fen.flottant ? 'Ancrer à droite' : 'Fenêtre flottante (déplaçable et redimensionnable)'}>
              {fen.flottant ? <PanelRight className="h-4 w-4" /> : <PictureInPicture2 className="h-4 w-4" />}
            </button>
            <button type="button" onClick={() => setReduit(true)} className="shrink-0 rounded-md p-1.5 hover:bg-white/10" data-testid="klarys-reduire" aria-label="Réduire" title="Réduire (la conversation est conservée)">
              <Minus className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => { setOuvert(false); setReduit(false); }} className="shrink-0 rounded-md p-1.5 hover:bg-white/10" data-testid="klarys-close" aria-label="Fermer" title="Fermer (Échap)">
              <X className="h-4 w-4" />
            </button>
          </header>
          <div className="min-h-0 flex-1">
            {vue === 'chat' && <KlarysChat headers={getAuthHeader()} cible={cible} demandeExterne={demandeExterne} onDemandeTraitee={() => setDemandeExterne(null)} />}
            {vue === 'partages' && <KlarysPartages headers={getAuthHeader()} onLancer={lancerDemande} onVu={() => setNonLus(0)} />}
            {vue === 'taches' && <KlarysTaches headers={getAuthHeader()} superAdmin={superAdmin} />}
            {vue === 'objectifs' && <KlarysObjectifs headers={getAuthHeader()} superAdmin={superAdmin} />}
            {vue === 'envois' && <KlarysEnvois headers={getAuthHeader()} />}
            {vue === 'journal' && <KlarysJournal headers={getAuthHeader()} />}
            {vue === 'memoire' && <KlarysMemoire headers={getAuthHeader()} />}
            {vue === 'attribuer' && <KlarysAttribuer headers={getAuthHeader()} onCompte={setAAttribuer} />}
          </div>
        </div>
      )}
    </>
  );
};
